"use client";

import React, { useRef } from "react";
import { gsap, ScrollTrigger } from "@/lib/gsap";
import { SplitText } from "gsap/SplitText";
import { useGSAP } from "@gsap/react";

if (typeof window !== "undefined") {
  gsap.registerPlugin(SplitText);
}

interface ScrollTextAnimationProps {
  children: React.ReactNode;
  colorInitial?: string;
  colorAccent?: string;
  colorFinal?: string;
  className?: string;
  stagger?: number;
  speed?: number;
  startTrigger?: string;
  endTrigger?: string;
}

export default function ScrollTextAnimation({
  children,
  colorInitial = "#cfd1d2",
  colorAccent = "#003c40",
  colorFinal = "#001640",
  className = "",
  stagger = 0.02,
  speed = 1,
  startTrigger = "top 100%",
  endTrigger = "top 20%",
}: ScrollTextAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const splitRefs = useRef<
    Array<{ wordSplit: SplitText; charSplit: SplitText }>
  >([]);

  useGSAP(
    () => {
      if (!containerRef.current) return;

      // Function to remove aria-label from all elements
      const removeAriaLabels = (element: HTMLElement) => {
        if (element.hasAttribute("aria-label")) {
          element.removeAttribute("aria-label");
        }
        const allElements = element.querySelectorAll("*");
        allElements.forEach((el) => {
          if (el instanceof HTMLElement && el.hasAttribute("aria-label")) {
            el.removeAttribute("aria-label");
          }
        });
      };

      // Remove aria-label from container and child elements if present
      removeAriaLabels(containerRef.current);

      splitRefs.current.forEach(({ wordSplit, charSplit }) => {
        wordSplit.revert();
        charSplit.revert();
      });
      splitRefs.current = [];

      const wordSplit = SplitText.create(containerRef.current, {
        type: "words",
        wordsClass: "scroll-animated-word",
      });

      const charSplit = SplitText.create(wordSplit.words, {
        type: "chars",
        charsClass: "scroll-animated-char",
      });

      splitRefs.current.push({ wordSplit, charSplit });

      const allChars = charSplit.chars;

      // Remove aria-label from all dynamically created elements
      // Use setTimeout to ensure SplitText has finished creating elements
      setTimeout(() => {
        if (containerRef.current) {
          removeAriaLabels(containerRef.current);
        }

        // Also remove from words and chars specifically
        if (wordSplit.words) {
          wordSplit.words.forEach((word) => {
            if (
              word instanceof HTMLElement &&
              word.hasAttribute("aria-label")
            ) {
              word.removeAttribute("aria-label");
            }
          });
        }
        if (charSplit.chars) {
          charSplit.chars.forEach((char) => {
            if (
              char instanceof HTMLElement &&
              char.hasAttribute("aria-label")
            ) {
              char.removeAttribute("aria-label");
            }
          });
        }
      }, 0);

      // Use MutationObserver to watch for any aria-label additions
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "aria-label"
          ) {
            const target = mutation.target;
            if (
              target instanceof HTMLElement &&
              target.hasAttribute("aria-label")
            ) {
              target.removeAttribute("aria-label");
            }
          }
          if (mutation.type === "childList") {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof HTMLElement) {
                // Set role="presentation" on new elements
                if (!node.hasAttribute("role")) {
                  node.setAttribute("role", "presentation");
                }
                removeAriaLabels(node);
              }
            });
          }
        });
      });

      // Observe the container for any changes
      observer.observe(containerRef.current, {
        attributes: true,
        attributeFilter: ["aria-label"],
        childList: true,
        subtree: true,
      });

      gsap.set(allChars, { color: colorInitial });

      ScrollTrigger.create({
        trigger: containerRef.current,
        start: startTrigger,
        end: endTrigger,
        scrub: 1 / speed,
        onUpdate: (self) => {
          const progress = self.progress;

          allChars.forEach((char, index) => {
            const adjustedStagger = stagger / speed;
            const charProgress = Math.max(
              0,
              Math.min(1, progress - index * adjustedStagger)
            );

            if (charProgress > 0) {
              const currentProgress = charProgress;

              if (currentProgress < 0.5) {
                const localProgress = currentProgress * 2;
                gsap.set(char, {
                  color: gsap.utils.interpolate(
                    colorInitial,
                    colorAccent,
                    localProgress
                  ),
                });
              } else {
                const localProgress = (currentProgress - 0.5) * 2;
                gsap.set(char, {
                  color: gsap.utils.interpolate(
                    colorAccent,
                    colorFinal,
                    localProgress
                  ),
                });
              }
            }
          });
        },
      });

      return () => {
        observer.disconnect();
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.vars.trigger === containerRef.current) {
            trigger.kill();
          }
        });

        splitRefs.current.forEach(({ wordSplit, charSplit }) => {
          wordSplit.revert();
          charSplit.revert();
        });
      };
    },
    {
      scope: containerRef,
      dependencies: [
        children,
        colorInitial,
        colorAccent,
        colorFinal,
        speed,
        startTrigger,
        endTrigger,
      ],
    }
  );

  return (
    <div ref={containerRef} className={className}>
      {children}
    </div>
  );
}
